/**
 * Project Billing Configuration Component
 *
 * Manages project-level billing configurations with independent timelines for:
 * - Discount percentage
 * - Minimum billable days
 * - Maximum billable days
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardHeader,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format, parseISO, isBefore } from 'date-fns';
import billingConfigService from '../../services/billingConfigService';
import ConfirmDialog from '../Common/ConfirmDialog';

const ProjectBillingConfig = ({ projectCode }) => {
  const [activeTab, setActiveTab] = useState(0); // 0=discount, 1=min, 2=max
  const [discountConfigs, setDiscountConfigs] = useState([]);
  const [minDaysConfigs, setMinDaysConfigs] = useState([]);
  const [maxDaysConfigs, setMaxDaysConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    effective_from: '',
    end_date: '',
    value: '',
  });

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);

  useEffect(() => {
    loadAllConfigs();
  }, [projectCode]);

  const loadAllConfigs = async () => {
    try {
      setLoading(true);
      const [discount, minDays, maxDays] = await Promise.all([
        billingConfigService.getProjectDiscountConfigs(projectCode),
        billingConfigService.getProjectMinDaysConfigs(projectCode),
        billingConfigService.getProjectMaxDaysConfigs(projectCode),
      ]);
      setDiscountConfigs(discount);
      setMinDaysConfigs(minDays);
      setMaxDaysConfigs(maxDays);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type, config = null) => {
    setEditingConfig(config);
    if (config) {
      setFormData({
        effective_from: config.effective_from,
        end_date: config.end_date || '',
        value: getValueFromConfig(config, type),
      });
    } else {
      // Default to first of next month
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(1);
      setFormData({
        effective_from: format(nextMonth, 'yyyy-MM-01'),
        end_date: '',
        value: type === 'discount' ? '0' : type === 'min_days' ? '0' : '31',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConfig(null);
    setFormData({ effective_from: '', end_date: '', value: '' });
    setError('');
  };

  const getValueFromConfig = (config, type) => {
    if (type === 'discount') return config.discount_percentage;
    if (type === 'min_days') return config.min_billable_days;
    return config.max_billable_days;
  };

  const getConfigType = () => {
    if (activeTab === 0) return 'discount';
    if (activeTab === 1) return 'min_days';
    return 'max_days';
  };

  const validateForm = () => {
    const type = getConfigType();
    const value = parseFloat(formData.value);

    if (!formData.effective_from) {
      setError('Effective date is required');
      return false;
    }

    // Validate effective_from is first of month
    const date = parseISO(formData.effective_from);
    if (date.getDate() !== 1) {
      setError('Effective date must be the first day of a month');
      return false;
    }

    // Validate end_date if provided
    if (formData.end_date) {
      const endDate = parseISO(formData.end_date);
      const lastDayOfMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate();

      if (endDate.getDate() !== lastDayOfMonth) {
        setError('End date must be the last day of a month');
        return false;
      }
      if (endDate < date) {
        setError('End date must be on or after effective date');
        return false;
      }
    }

    if (formData.value === '' || isNaN(value)) {
      setError('Value is required and must be a number');
      return false;
    }

    if (type === 'discount' && (value < 0 || value > 100)) {
      setError('Discount must be between 0 and 100');
      return false;
    }

    if (type === 'min_days' && value < 0) {
      setError('Min billable days must be >= 0');
      return false;
    }

    if (type === 'max_days' && value <= 0) {
      setError('Max billable days must be > 0');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setError('');
      const type = getConfigType();
      const value = parseFloat(formData.value);
      const data = {
        effective_from: formData.effective_from,
        end_date: formData.end_date || null,
        [type === 'discount' ? 'discount_percentage' : type === 'min_days' ? 'min_billable_days' : 'max_billable_days']: value,
      };

      if (editingConfig) {
        // Update existing config
        if (type === 'discount') {
          await billingConfigService.updateProjectDiscountConfig(projectCode, editingConfig.id, data);
        } else if (type === 'min_days') {
          await billingConfigService.updateProjectMinDaysConfig(projectCode, editingConfig.id, data);
        } else {
          await billingConfigService.updateProjectMaxDaysConfig(projectCode, editingConfig.id, data);
        }
        setSuccess('Configuration updated successfully');
      } else {
        // Create new config
        if (type === 'discount') {
          await billingConfigService.createProjectDiscountConfig(projectCode, data);
        } else if (type === 'min_days') {
          await billingConfigService.createProjectMinDaysConfig(projectCode, data);
        } else {
          await billingConfigService.createProjectMaxDaysConfig(projectCode, data);
        }
        setSuccess('Configuration created successfully');
      }

      await loadAllConfigs();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    }
  };

  const handleDeleteClick = (config) => {
    setConfigToDelete(config);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!configToDelete) return;

    try {
      const type = getConfigType();
      if (type === 'discount') {
        await billingConfigService.deleteProjectDiscountConfig(projectCode, configToDelete.id);
      } else if (type === 'min_days') {
        await billingConfigService.deleteProjectMinDaysConfig(projectCode, configToDelete.id);
      } else {
        await billingConfigService.deleteProjectMaxDaysConfig(projectCode, configToDelete.id);
      }

      setSuccess('Configuration deleted successfully');
      await loadAllConfigs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete configuration');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setConfigToDelete(null);
  };

  const isActiveConfig = (config) => {
    const today = new Date();
    const configDate = parseISO(config.effective_from);
    return isBefore(configDate, today) || format(configDate, 'yyyy-MM-01') === format(today, 'yyyy-MM-01');
  };

  const renderConfigTable = (configs, type) => {
    const getValueLabel = (config) => {
      if (type === 'discount') return `${config.discount_percentage}%`;
      if (type === 'min_days') return `${config.min_billable_days} days`;
      return `${config.max_billable_days} days`;
    };

    const getColumnLabel = () => {
      if (type === 'discount') return 'Discount %';
      if (type === 'min_days') return 'Min Days';
      return 'Max Days';
    };

    return (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Effective From</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>End Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{getColumnLabel()}</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No configurations found. System defaults will be used.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    {format(parseISO(config.effective_from), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {config.end_date ? format(parseISO(config.end_date), 'MMM dd, yyyy') : 'Ongoing'}
                  </TableCell>
                  <TableCell>{getValueLabel(config)}</TableCell>
                  <TableCell>
                    {isActiveConfig(config) ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Future" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleOpenDialog(type, config)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDeleteClick(config)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Card>
        <CardHeader
          title={`Billing Configuration - ${projectCode}`}
          subheader="Configure discount percentage, min/max billable days with independent date ranges"
        />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Discount %" />
            <Tab label="Min Billable Days" />
            <Tab label="Max Billable Days" />
          </Tabs>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog(getConfigType())}
            >
              Add Configuration
            </Button>
          </Box>

          {activeTab === 0 && renderConfigTable(discountConfigs, 'discount')}
          {activeTab === 1 && renderConfigTable(minDaysConfigs, 'min_days')}
          {activeTab === 2 && renderConfigTable(maxDaysConfigs, 'max_days')}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingConfig ? 'Edit Configuration' : 'Add Configuration'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            label="Effective From"
            type="month"
            fullWidth
            margin="normal"
            value={formData.effective_from ? formData.effective_from.substring(0, 7) : ''}
            onChange={(e) => setFormData({ ...formData, effective_from: e.target.value + '-01' })}
            helperText="Configuration becomes active from the first day of this month"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="End Date (Optional)"
            type="month"
            fullWidth
            margin="normal"
            value={formData.end_date ? formData.end_date.substring(0, 7) : ''}
            onChange={(e) => {
              if (e.target.value) {
                // Calculate last day of selected month
                const [year, month] = e.target.value.split('-');
                const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
                setFormData({ ...formData, end_date: `${e.target.value}-${lastDay.toString().padStart(2, '0')}` });
              } else {
                setFormData({ ...formData, end_date: '' });
              }
            }}
            helperText="Config is active through the end of this month."
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label={
              activeTab === 0 ? 'Discount Percentage' :
              activeTab === 1 ? 'Min Billable Days' :
              'Max Billable Days'
            }
            type="number"
            fullWidth
            margin="normal"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: e.target.value })}
            helperText={
              activeTab === 0 ? 'Enter discount percentage (0-100)' :
              activeTab === 1 ? 'Minimum billable days per month' :
              'Maximum billable days per month'
            }
            inputProps={{
              min: activeTab === 2 ? 1 : 0,
              max: activeTab === 0 ? 100 : undefined,
              step: activeTab === 0 ? 0.1 : 1,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingConfig ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Configuration"
        message="Are you sure you want to delete this billing configuration?"
        details={configToDelete ? [
          { label: 'Type', value: activeTab === 0 ? 'Discount %' : activeTab === 1 ? 'Min Billable Days' : 'Max Billable Days' },
          { label: 'Value', value: activeTab === 0 ? `${configToDelete.discount_percentage}%` : activeTab === 1 ? `${configToDelete.min_billable_days} days` : `${configToDelete.max_billable_days} days` },
          { label: 'Effective From', value: format(parseISO(configToDelete.effective_from), 'MMM dd, yyyy') },
        ] : []}
      />
    </Container>
  );
};

export default ProjectBillingConfig;
